import { useTracking } from 'react-tracking';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { useFlag } from '@simplywallst/unleash';

import { useSnackbar } from '@/features/snackbar';
import { useBraze } from '@/components/Braze';

import { QUERY_KEY as DASHBOARD_FEED } from '@/components/Dashboard/hooks/useFeed';

import {
  WATCHLIST_QUERY_KEY,
  WATCHLISTS_QUERY_KEY,
} from '@/hooks/useWatchlist';
import type { WatchlistsQueryClientData } from '@/hooks/useWatchlist';

import { useSelectedWatchlist } from '../useSelectedWatchlist';
import useWatchlistQueryClient from '../useWatchlistQueryClient';

import { useCreateWatchlist } from '@/hooks/useWatchlistMutations';

import { addQueryFn } from './addQueryFn';
import { removeQueryFn } from './removeQueryFn';
import { renameWatchlistQueryFn } from './renameWatchlistQueryFn';
import { deleteWatchlistQueryFn } from './deleteWatchlistQueryFn';

export function useWatchlistMutations() {
  const willDeprecateStream = useFlag('stream-from-database');

  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const { addSnackbar } = useSnackbar();
  const { logCustomEvent } = useBraze();
  const watchlistQueryClient = useWatchlistQueryClient();
  const [selectedWatchlistId, selectWatchlist] = useSelectedWatchlist();

  const { mutate: addCompany, status: addCompanyStatus } = useMutation({
    mutationFn: addQueryFn,
    onMutate: ({ includeSnackBar }) => {
      if (includeSnackBar) {
        addSnackbar({
          type: 'loading',
          message: 'Adding company to Watchlist',
          lifeTime: 2000,
        });
      }
    },
    onSuccess: (data, { watchlistId, includeSnackBar, autoAdded }) => {
      logCustomEvent('company_added', {
        add_to: 'watchlist',
        add_to_action: autoAdded ? 'auto' : 'manual',
        company_name: data.company.name,
        company_ticker: data.company.uniqueSymbol,
        company_market: data.company.exchangeCountryISO,
        company_industry_lvl_2:
          data.company.secondaryIndustry?.name ?? 'unknown',
      });

      trackEvent({
        action: 'add_stock',
        name: data.company.name,
        unique_symbol: data.company.uniqueSymbol,
        market: data.company.exchangeCountryISO,
      });

      if (includeSnackBar) {
        const id = data.company.tickerSymbol;
        addSnackbar({
          type: 'positive',
          message: `${id} added to Watchlist`,
          lifeTime: 2000,
        });
      }

      watchlistQueryClient.setQueriesData(watchlistId, (state) => {
        const alreadyExists = state.items.some(
          (item) => item.companyId === data.companyId
        );
        if (alreadyExists) {
          return state;
        }
        return produce(state, (draft) => {
          draft.items.unshift(data);
        });
      });

      queryClient.setQueryData<WatchlistsQueryClientData>(
        WATCHLISTS_QUERY_KEY,
        (state) => {
          if (typeof state === 'undefined') {
            return state;
          }
          return produce(state, (draft) => {
            const updatedWatchlist = draft.find((w) => w.id === watchlistId);
            if (updatedWatchlist) {
              updatedWatchlist.items.unshift({ companyId: data.companyId });
              updatedWatchlist.totalItems++;
            }
          });
        }
      );
    },
    onSettled: (_, __, payload) => {
      queryClient.invalidateQueries({ queryKey: WATCHLISTS_QUERY_KEY });
      watchlistQueryClient.invalidateQueries(payload.watchlistId);

      if (willDeprecateStream.enabled) {
        // Invalidate the dashboard feed query to ensure it reflects the latest data
        // e.g. it includes all the companies/holdings that should be in the feed
        queryClient.invalidateQueries({ queryKey: DASHBOARD_FEED });
      }
    },
    onError: (error: Error) => {
      let message = 'Failed to add company';
      if (error && error.message.includes('already in watchlist')) {
        message = 'This company is already in your watchlist';
      }
      addSnackbar({
        type: 'negative',
        message,
        lifeTime: 2000,
      });
    },
  });

  const { mutate: removeCompany, status: removeCompanyStatus } = useMutation({
    mutationFn: removeQueryFn,
    onMutate: ({ companyId, watchlistId }) => {
      /** Optimistically remove key */
      watchlistQueryClient.setQueriesData(watchlistId, (state) =>
        produce(state, (draft) => {
          draft.items = draft.items.filter(
            (item) => item.companyId !== companyId
          );
        })
      );

      queryClient.setQueryData<WatchlistsQueryClientData>(
        WATCHLISTS_QUERY_KEY,
        (state) => {
          if (typeof state === 'undefined') {
            return state;
          }
          return produce(state, (draft) => {
            const updatedWatchlist = draft.find((w) => w.id === watchlistId);
            const removedIndex =
              updatedWatchlist?.items.findIndex(
                (i) => i.companyId === companyId
              ) ?? -1;
            if (updatedWatchlist && removedIndex !== -1) {
              updatedWatchlist.items.splice(removedIndex, 1);
              updatedWatchlist.totalItems--;
            }
          });
        }
      );
    },
    onSuccess: (deleted, { includeSnackBar, message }) => {
      if (deleted && includeSnackBar) {
        addSnackbar({
          type: 'positive',
          message: message || 'Removed from watchlist',
          lifeTime: 2000,
        });
      }
    },
    onSettled: (_, __, payload) => {
      queryClient.invalidateQueries({ queryKey: WATCHLISTS_QUERY_KEY });
      watchlistQueryClient.invalidateQueries(payload.watchlistId);

      if (willDeprecateStream.enabled) {
        // Invalidate the dashboard feed query to ensure it reflects the latest data
        // e.g. it includes all the companies/holdings that should be in the feed
        queryClient.invalidateQueries({ queryKey: DASHBOARD_FEED });
      }
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        message: 'Failed to remove company',
        lifeTime: 1500,
      });
    },
  });

  const { mutate: createWatchlist, status: createWatchlistStatus } =
    useCreateWatchlist({
      onMutate: () => {
        addSnackbar({
          type: 'loading',
          message: 'Creating list...',
          lifeTime: 2000,
        });
      },
      onSuccess: (watchlist) => {
        trackEvent({
          action: 'success',
          modifier: 'createWatchlist',
        });
        addSnackbar({
          type: 'positive',
          message: 'New list created successfully',
          lifeTime: 2000,
        });
        selectWatchlist(watchlist.id);
      },
      onError: () => {
        const message = 'Failed to create new watchlist';
        addSnackbar({
          type: 'negative',
          message,
          lifeTime: 2000,
        });
      },
    });

  const { mutate: renameWatchlist, status: renameWatchlistStatus } =
    useMutation({
      mutationFn: renameWatchlistQueryFn,
      onMutate: ({ id: watchlistId, newName }) => {
        addSnackbar({
          type: 'loading',
          message: 'Renaming list...',
          lifeTime: 2000,
        });
        /** Optimistically rename watchlist */
        watchlistQueryClient.setQueriesData(watchlistId, (state) =>
          produce(state, (draft) => {
            draft.name = newName;
          })
        );
        queryClient.setQueryData<WatchlistsQueryClientData>(
          WATCHLISTS_QUERY_KEY,
          (state) => {
            if (typeof state === 'undefined') {
              return state;
            }
            return produce(state, (draft) => {
              const renamedWatchlist = draft.find(
                ({ id }) => id === watchlistId
              );
              if (renamedWatchlist) {
                renamedWatchlist.name = newName;
              }
            });
          }
        );
      },
      onSuccess: (watchlist) => {
        const { name } = watchlist;
        trackEvent({
          action: 'success',
          modifier: 'renameWatchlist',
        });
        addSnackbar({
          type: 'positive',
          message: `List successfully renamed to ${name}`,
          lifeTime: 2000,
        });
      },
      onSettled: (_, __, payload) => {
        watchlistQueryClient.invalidateQueries(payload.id);
        queryClient.invalidateQueries({ queryKey: WATCHLISTS_QUERY_KEY });
      },
      onError: () => {
        const message = 'Failed to rename watchlist';
        addSnackbar({
          type: 'negative',
          message,
          lifeTime: 2000,
        });
      },
    });

  const { mutate: deleteWatchlist, status: deleteWatchlistStatus } =
    useMutation({
      mutationFn: deleteWatchlistQueryFn,
      onMutate: (payload) => {
        addSnackbar({
          type: 'loading',
          message: 'Deleting list...',
          lifeTime: 1000,
        });

        queryClient.setQueryData<WatchlistsQueryClientData>(
          WATCHLISTS_QUERY_KEY,
          (state) => state?.filter((w) => w.id !== payload.id)
        );
      },
      onSuccess: async (deleted, { id: deletedId }) => {
        if (deleted) {
          trackEvent({
            action: 'success',
            modifier: 'deleteWatchlist',
          });
          addSnackbar({
            type: 'positive',
            message: 'List successfully deleted',
            lifeTime: 2000,
          });

          if (selectedWatchlistId === deletedId) {
            selectWatchlist(null);
          }
          /**
           * Remove query specifically for deletedId, then refetch queries matching
           * deletedId in case the `null` query was for the same id. Ensure refetch
           * completes before invalidating the watchlists query by awaiting it.
           */
          queryClient.removeQueries({
            queryKey: [WATCHLIST_QUERY_KEY, deletedId],
          });
          await watchlistQueryClient.refetchQueries(deletedId);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: WATCHLISTS_QUERY_KEY });
      },
      onError: () => {
        addSnackbar({
          type: 'negative',
          message: 'Failed to delete list',
          lifeTime: 1500,
        });
      },
    });

  return {
    addCompany,
    removeCompany,
    createWatchlist,
    renameWatchlist,
    deleteWatchlist,
    addCompanyStatus,
    createWatchlistStatus,
    removeCompanyStatus,
    renameWatchlistStatus,
    deleteWatchlistStatus,
  };
}
